import { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import mux from 'mux-embed';
import { useSwiperSlide } from 'swiper/react/swiper-react';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  className?: string;
}

const muxEnvKey = process.env.REACT_APP_MUX_ENVIRONMENT_KEY;

export default function VideoPlayer({ src, poster, className }: VideoPlayerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const swiperSlide = useSwiperSlide();

  useEffect(() => {
    let hls: Hls | undefined;
    if (videoRef.current) {
      const video = videoRef.current;
      const initTime = Date.now();

      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
      } else if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(video);
      }

      if (muxEnvKey) {
        mux.monitor(video, {
          debug: false,
          hlsjs: hls,
          Hls,
          data: {
            env_key: muxEnvKey,
            player_name: 'Slider Player',
            player_init_time: initTime,
          },
        });
      }
    }

    return () => {
      const video = videoRef.current;
      if (video && !video.paused) {
        video.pause();
      }
      if (hls) {
        hls.destroy();
      }
    };
  }, [videoRef, src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (!swiperSlide.isActive && !video.paused) {
      video.pause();
    }
  }, [src, swiperSlide.isActive]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      controls
      ref={videoRef}
      className={className}
      width="100%"
      height="100%"
      poster={poster}
      autoPlay={!swiperSlide.isDuplicate}
      playsInline
      loop={false}
    />
  );
}

const defaultProps: Pick<VideoPlayerProps, 'className' | 'poster'> = {
  className: undefined,
  poster: undefined,
};

VideoPlayer.defaultProps = defaultProps;
